"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import staticServices from "./data/services";
import PremiumButton from "./PremiumButton";
import { Send, CheckCircle, MessageSquare } from "lucide-react";
import { fetchWithCsrf } from "../lib/csrfClient";

const Contact = ({ backgroundOverride = null }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    projectType: "",
    budget: "",
    timeline: "",
    message: "",
    maintenance: false,
  });

  const MESSAGE_MAX_LENGTH = 2000;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [services, setServices] = useState(staticServices); // Start with static data

  useEffect(() => {
    fetchServices();
    
    // Check for URL parameter to pre-select mockup offer
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('offer') === 'mockup') {
      setFormData(prev => ({
        ...prev,
        projectType: 'Free Website Mockup (Limited Time Offer)'
      }));
    }
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch("/api/services");
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (error) {
      // Silently fail and use default services
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetchWithCsrf("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          maintenance: false // Always set to false since checkbox is removed
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: "",
          email: "",
          company: "",
          phone: "",
          projectType: "",
          budget: "",
          timeline: "",
          message: "",
          maintenance: false,
        });
      } else {
        const data = await response.json();
        setError(data.error || "Failed to send message");
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.section
      id="contact"
      className={`py-24 px-6 scroll-mt-28 ${backgroundOverride || 'bg-[var(--bg-secondary)]'} text-[var(--text-primary)] relative overflow-hidden`}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      {/* Single ember glow */}
      <div className="absolute top-0 left-0 right-0 h-px bg-[var(--border-color)]"></div>
      <div className="absolute top-1/2 -translate-y-1/2 right-0 w-[32rem] h-[32rem] rounded-full bg-[var(--brand-color)]/6 blur-[130px] -z-10 pointer-events-none"></div>

      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-3 gap-12">
          {/* Left column - intro text */}
          <div className="md:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="eyebrow mb-6">
                <span className="text-[var(--brand-color)]">06</span> · Get
                Started
              </p>

              <h2
                className="font-display font-medium tracking-tight leading-[1.05] mb-5"
                style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}
              >
                Let's talk about your{" "}
                <em className="italic text-[var(--brand-color)]">project</em>.
              </h2>

              <p className="text-[var(--text-secondary)] mb-8 text-lg leading-relaxed">
                Tell me about your project and I will get back to you within 24
                hours.
              </p>

              <div className="mb-10">
                <PremiumButton
                  href="/services"
                  size="lg"
                  className="mb-3"
                  gaEvent={{
                    action: 'cta_click',
                    category: 'Contact',
                    label: 'View Services - Contact Sidebar',
                    parameters: {
                      button_text: 'View Services',
                      button_location: 'contact_sidebar',
                      destination_url: '/services'
                    }
                  }}
                >
                  View Services
                </PremiumButton>
                <p className="text-sm text-[var(--text-secondary)]">
                  Most projects start within 48 hours
                </p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--brand-color)]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle
                      size={16}
                      className="text-[var(--brand-color)]"
                    />
                  </div>
                  <p className="text-sm text-[var(--text-secondary)]">
                    Timely project completion
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--brand-color)]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle
                      size={16}
                      className="text-[var(--brand-color)]"
                    />
                  </div>
                  <p className="text-sm text-[var(--text-secondary)]">
                    100% client ownership
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--brand-color)]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle
                      size={16}
                      className="text-[var(--brand-color)]"
                    />
                  </div>
                  <p className="text-sm text-[var(--text-secondary)]">
                    No required subscriptions
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right column - form */}
          <motion.div
            className="md:col-span-2"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="relative">
              <div className="relative bg-[var(--bg-tertiary)]/40 p-8 rounded-sm border border-[var(--border-color)]">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircle
                      size={48}
                      className="text-[var(--brand-color)] mx-auto mb-4"
                    />
                    <h3 className="text-xl font-display font-medium tracking-tight mb-2">
                      Message Sent
                    </h3>
                    <p className="text-[var(--text-secondary)]">
                      Thank you for your message. I will get back to you within
                      24 hours.
                    </p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="mt-4 text-[var(--brand-color)] hover:underline"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          className="block text-sm text-[var(--text-secondary)] mb-2"
                          htmlFor="name"
                        >
                          Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-[var(--bg-primary)]/60 border border-[var(--border-color)] rounded-sm text-sm focus:outline-none focus:border-[var(--brand-color)] focus:ring-1 focus:ring-[var(--brand-color)] transition"
                        />
                      </div>

                      <div>
                        <label
                          className="block text-sm text-[var(--text-secondary)] mb-2"
                          htmlFor="email"
                        >
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-[var(--bg-primary)]/60 border border-[var(--border-color)] rounded-sm text-sm focus:outline-none focus:border-[var(--brand-color)] focus:ring-1 focus:ring-[var(--brand-color)] transition"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        className="block text-sm text-[var(--text-secondary)] mb-2"
                        htmlFor="type"
                      >
                        What type of service do you need?
                      </label>
                      <select
                        name="projectType"
                        id="projectType"
                        value={formData.projectType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-[var(--bg-primary)]/60 border border-[var(--border-color)] rounded-sm text-sm text-[var(--text-secondary)] focus:outline-none focus:border-[var(--brand-color)] focus:ring-1 focus:ring-[var(--brand-color)] transition appearance-none"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23ff5a1f' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 1rem center",
                        }}
                      >
                        <option value="">Select an option</option>
                        <option value="Free Website Mockup (Limited Time Offer)" className="font-semibold text-[var(--brand-color)]">
                          Free Website Mockup (Limited Time Offer)
                        </option>
                        {services
                          .filter(service => service.serviceType === "WEBSITE_CREATION")
                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((service, index) => (
                            <option key={service.id || index} value={service.title}>
                              {service.title}
                            </option>
                          ))
                        }
                        {/* Fallback options if no services loaded */}
                        {services.filter(service => service.serviceType === "WEBSITE_CREATION").length === 0 && (
                          <>
                            <option value="One-Page Landing Page">One-Page Landing Page</option>
                            <option value="Custom Website">Custom Website</option>
                          </>
                        )}
                      </select>
                    </div>

                    <div>
                      <label
                        className="block text-sm text-[var(--text-secondary)] mb-2"
                        htmlFor="message"
                      >
                        Project Details
                      </label>
                      <textarea
                        name="message"
                        id="message"
                        rows="5"
                        value={formData.message}
                        onChange={handleInputChange}
                        maxLength={MESSAGE_MAX_LENGTH}
                        required
                        placeholder="What's the project about, and when do you need it live? Include any specific features, design preferences, integrations, or technical requirements."
                        className="w-full px-4 py-3 bg-[var(--bg-primary)]/60 border border-[var(--border-color)] rounded-sm text-sm focus:outline-none focus:border-[var(--brand-color)] focus:ring-1 focus:ring-[var(--brand-color)] transition resize-y min-h-[120px] max-h-[400px]"
                      ></textarea>
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-xs text-[var(--text-secondary)]">
                          The more details you provide, the better I can help you.
                        </p>
                        <span className={`text-xs ${formData.message.length > MESSAGE_MAX_LENGTH * 0.9 ? 'text-orange-400' : 'text-[var(--text-secondary)]'}`}>
                          {formData.message.length}/{MESSAGE_MAX_LENGTH}
                        </span>
                      </div>
                    </div>

                    {error && (
                      <div className="text-red-400 text-sm text-center">
                        {error}
                      </div>
                    )}

                    <div className="flex justify-center">
                      <PremiumButton
                        type="submit"
                        as="motion.button"
                        disabled={isSubmitting}
                        size="md"
                        className="shadow-lg"
                        gaEvent={{
                          action: 'form_submit',
                          category: 'Contact',
                          label: 'Send Message - Contact Form',
                          parameters: {
                            button_text: 'Send Message',
                            button_location: 'contact_form',
                            form_type: 'contact_form'
                          }
                        }}
                      >
                        {isSubmitting ? "Sending..." : "Send Message"}
                        <Send size={16} />
                      </PremiumButton>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default Contact;
